/**
 * Formatting utilities for the Aircraft Floating Card
 *
 * Currency, date, client name formatting, and alert computation.
 */

import type {
  AircraftClientInfo,
  AllocationEnrichedData,
  CardAlert,
} from '@/types/aircraft-card'

/**
 * Format a number as USD currency
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

/**
 * Format an ISO date string as "Jan 15, 2026"
 */
export function formatDate(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/**
 * Format relative time like "in 5 days", "3 days ago", "today"
 */
export function formatRelativeDate(dateStr: string): string {
  const days = getDaysUntil(dateStr)
  if (days === 0) return 'today'
  if (days === 1) return 'tomorrow'
  if (days === -1) return 'yesterday'
  if (days > 0) return `in ${days} days`
  return `${Math.abs(days)} days ago`
}

/**
 * Get number of days until a date (negative = past)
 */
export function getDaysUntil(dateStr: string): number {
  const target = new Date(dateStr)
  const now = new Date()
  // Zero out time components for day-level comparison
  target.setHours(0, 0, 0, 0)
  now.setHours(0, 0, 0, 0)
  return Math.round((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
}

/**
 * Format client display name from client info
 */
export function formatClientName(client: AircraftClientInfo | null): string {
  if (!client) return 'Unknown'
  if (client.companyName) return client.companyName
  if (client.firstName || client.lastName) {
    return [client.firstName, client.lastName].filter(Boolean).join(' ')
  }
  return 'Unknown'
}

/**
 * Format dimensions as "L x W x H ft"
 */
export function formatDimensions(
  length: number | null,
  wingspan: number | null,
  height: number | null
): string | null {
  if (!length && !wingspan && !height) return null
  const parts = [
    length ? `${length.toFixed(1)}L` : null,
    wingspan ? `${wingspan.toFixed(1)}W` : null,
    height ? `${height.toFixed(1)}H` : null,
  ].filter(Boolean)
  return parts.join(' x ') + ' ft'
}

/**
 * Format weight as "X,XXX lbs"
 */
export function formatWeight(weight: number | null): string | null {
  if (!weight) return null
  return `${weight.toLocaleString('en-US')} lbs`
}

/**
 * Format relative time with hour/minute granularity for near-term events.
 * Returns "in 45m", "in 3h", "in 2d", "45m ago", "3h ago", etc.
 */
export function formatRelativeTimeShort(dateStr: string): string {
  const target = new Date(dateStr)
  const now = new Date()
  const diffMs = target.getTime() - now.getTime()
  const absDiffMs = Math.abs(diffMs)
  const isFuture = diffMs > 0

  const minutes = Math.round(absDiffMs / (1000 * 60))
  const hours = Math.round(absDiffMs / (1000 * 60 * 60))
  const days = Math.round(absDiffMs / (1000 * 60 * 60 * 24))

  let value: string
  if (minutes < 2) value = 'now'
  else if (minutes < 60) value = `${minutes}m`
  else if (hours < 24) value = `${hours}h`
  else value = `${days}d`

  if (value === 'now') return value
  return isFuture ? `in ${value}` : `${value} ago`
}

/**
 * Format time portion of an ISO date as "14:00" local time
 */
export function formatLocalTime(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

/**
 * Get hours since a timestamp (negative = future)
 */
export function getHoursSince(dateStr: string): number {
  const target = new Date(dateStr)
  const now = new Date()
  return (now.getTime() - target.getTime()) / (1000 * 60 * 60)
}

/**
 * Compute alerts from enriched allocation data.
 * Returns alerts sorted by priority (P1 first).
 */
export function computeAlerts(data: AllocationEnrichedData): CardAlert[] {
  const alerts: CardAlert[] = []

  // P1: Insurance expired
  if (data.aircraft.insuranceExpiry) {
    const days = getDaysUntil(data.aircraft.insuranceExpiry)
    if (days < 0) {
      alerts.push({
        type: 'insurance_expired',
        message: `Insurance expired ${formatRelativeDate(data.aircraft.insuranceExpiry)}`,
        severity: 'error',
        priority: 1,
        actionFormId: 'aircraft-details',
        actionField: 'insuranceExpiry',
      })
    } else if (days <= 30) {
      // P2: Insurance expiring soon
      alerts.push({
        type: 'insurance_expiring',
        message: `Insurance expires ${formatRelativeDate(data.aircraft.insuranceExpiry)}`,
        severity: days <= 7 ? 'error' : 'warning',
        priority: 2,
        actionFormId: 'aircraft-details',
        actionField: 'insuranceExpiry',
      })
    }
  }

  // P1: Collision
  if (data.allocation.hasCollision) {
    alerts.push({
      type: 'collision',
      message: 'Collision detected',
      severity: 'error',
      priority: 1,
    })
  }

  // P1: No active contract (on field > 24h)
  if (!data.leaseContract && !data.transientStay) {
    if (data.allocation.startsAt && getHoursSince(data.allocation.startsAt) > 24) {
      alerts.push({
        type: 'no_active_contract',
        message: 'No contract — Create?',
        severity: 'error',
        priority: 1,
        actionFormId: 'create-contract',
      })
    }
  }

  // P2: Lease contract expiring
  if (data.leaseContract?.endDate) {
    const days = getDaysUntil(data.leaseContract.endDate)
    if (days <= 30 && days >= 0) {
      alerts.push({
        type: 'contract_expiring',
        message: `Lease expires ${formatRelativeDate(data.leaseContract.endDate)}`,
        severity: days <= 7 ? 'error' : 'warning',
        priority: 2,
        actionFormId: 'lease-contract',
        actionField: 'endDate',
      })
    }
  }

  // P2: Transient departure within 24h
  if (data.transientStay?.expectedCheckOut && data.transientStay.status === 'checked_in') {
    const days = getDaysUntil(data.transientStay.expectedCheckOut)
    if (days === 0) {
      alerts.push({
        type: 'departure_within_24h',
        message: 'Departing today',
        severity: 'warning',
        priority: 2,
      })
    } else if (days === 1) {
      alerts.push({
        type: 'departure_within_24h',
        message: 'Departing tomorrow',
        severity: 'warning',
        priority: 2,
      })
    }
  }

  // P2: Overstaying (past expected checkout, still checked in)
  if (
    data.transientStay?.expectedCheckOut &&
    data.transientStay.status === 'checked_in' &&
    !data.transientStay.actualCheckOut
  ) {
    const days = getDaysUntil(data.transientStay.expectedCheckOut)
    if (days < 0) {
      const hours = Math.round(getHoursSince(data.transientStay.expectedCheckOut))
      alerts.push({
        type: 'overstaying',
        message: `Overstaying ${hours}h past checkout`,
        severity: 'warning',
        priority: 2,
      })
    }
  }

  // P1: Return overdue (expected back but hasn't arrived)
  if (data.allocation.presence === 'away' && data.allocation.expectedReturnAt) {
    const hours = getHoursSince(data.allocation.expectedReturnAt)
    if (hours > 2) { // 2-hour grace period
      alerts.push({
        type: 'return_overdue',
        message: `Return overdue (${Math.round(hours)}h late)`,
        severity: hours > 12 ? 'error' : 'warning',
        priority: 1,
      })
    }
  }

  // P3: No owner assigned
  if (!data.aircraft.owner && !data.aircraft.ownerName) {
    alerts.push({
      type: 'no_owner',
      message: 'No owner — Assign?',
      severity: 'info',
      priority: 3,
      actionFormId: 'aircraft-details',
      actionField: 'ownerClientId',
    })
  }

  // Sort by priority (P1 first)
  alerts.sort((a, b) => a.priority - b.priority)

  return alerts
}
